import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HeroPage.css';
import warehousePic from '../../images/hero_warehouse.jpg';
import freightPic from '../../images/offers-freight.jpg';
import deliveryPic from '../../images/offers-delivery.jpg';

const BG_IMAGES = [
  { src: warehousePic, alt: 'Renuit warehouse operations' },
  { src: freightPic,   alt: 'Freight transportation network' },
  { src: deliveryPic,  alt: 'Last-mile delivery fleet' },
];

const ArrowRight = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const ChevronDown = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6"/>
  </svg>
);

export const HeroPage = () => {
  const [activeIdx, setActiveIdx]   = useState(0);
  const [prevIdx,   setPrevIdx]     = useState<number | null>(null);
  const [direction, setDirection]   = useState<'left' | 'right'>('right');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const delay = setTimeout(() => {
      timerRef.current = setInterval(() => {
        setDirection(Math.random() < 0.3 ? 'left' : 'right');
        setActiveIdx(cur => {
          setPrevIdx(cur);
          return (cur + 1) % BG_IMAGES.length;
        });
      }, 5500);
    }, 800);
    return () => {
      clearTimeout(delay);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return (
    <section className="rn-hero">

      {/* Background image stack */}
      {BG_IMAGES.map((img, i) => (
        <img
          key={img.src}
          className={`rn-hero__bg${
            i === activeIdx
              ? prevIdx !== null
                ? ` rn-hero__bg--entering rn-hero__bg--entering-${direction}`
                : ' rn-hero__bg--visible'
              : i === prevIdx
              ? ' rn-hero__bg--leaving'
              : ''
          }`}
          src={img.src}
          alt={img.alt}
          fetchPriority={i === 0 ? 'high' : undefined}
          loading={i === 0 ? 'eager' : 'lazy'}
        />
      ))}

      {/* Gradient overlay */}
      <div className="rn-hero__gradient" aria-hidden="true" />

      {/* Decorative grid lines */}
      <div className="rn-hero__grid" aria-hidden="true">
        <span /><span /><span /><span />
      </div>

      {/* Main content */}
      <div className="rn-hero__content">
        <p className="rn-hero__eyebrow">
          <span className="rn-hero__pulse" />
          Deliver With Confidence
        </p>

        <h1 className="rn-hero__heading">
          Accelerate Your<br />
          Supply Chain With<br />
          <em>Expert Logistics</em>
        </h1>

        <p className="rn-hero__sub">
          Freight transportation, warehousing, and last-mile delivery across the
          Southeast, Gulf Coast, and East Coast — for businesses that can't afford delays.
        </p>

        <div className="rn-hero__actions">
          <Link to="/quote" className="rn-hero__btn rn-hero__btn--primary">
            Request a Quote <ArrowRight />
          </Link>
          <Link to="/services" className="rn-hero__btn rn-hero__btn--ghost">
            Explore Services
          </Link>
        </div>

        {/* Stats strip */}
        <div className="rn-hero__stats">
          <div className="rn-hero__stat">
            <span className="rn-hero__stat-num">12+</span>
            <span className="rn-hero__stat-label">Years Experience</span>
          </div>
          <span className="rn-hero__stat-divider" />
          <div className="rn-hero__stat">
            <span className="rn-hero__stat-num">96%</span>
            <span className="rn-hero__stat-label">On-Time Delivery</span>
          </div>
          <span className="rn-hero__stat-divider" />
          <div className="rn-hero__stat">
            <span className="rn-hero__stat-num">500+</span>
            <span className="rn-hero__stat-label">Clients Served</span>
          </div>
        </div>
      </div>

      {/* Slide dots */}
      <div className="rn-hero__dots" aria-label="Image navigation">
        {BG_IMAGES.map((_, i) => (
          <button
            key={i}
            className={`rn-hero__dot-btn${i === activeIdx ? ' is-active' : ''}`}
            onClick={() => { setPrevIdx(activeIdx); setDirection('right'); setActiveIdx(i); }}
            aria-label={`View image ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll cue */}
      <a href="#services" className="rn-hero__scroll" aria-label="Scroll to next section">
        <ChevronDown />
        <span>Scroll</span>
      </a>

    </section>
  );
};
